import Phaser from 'phaser';
import { BuildingData, BuildingStateData } from '../socket/SocketManager';

export abstract class BaseInterior {
  protected graphics: Phaser.GameObjects.Graphics;
  protected elements: Phaser.GameObjects.GameObject[] = [];
  protected exitCallback: (() => void) | null = null;

  constructor(
    protected scene: Phaser.Scene,
    protected building: BuildingData,
    protected stateData: BuildingStateData | null,
  ) {
    this.graphics = scene.add.graphics();
  }

  abstract render(): void;
  abstract bindEvents(): void;

  onExit(callback: () => void): void {
    this.exitCallback = callback;
  }

  protected drawRoom(floorColor: number, wallColor: number): void {
    const { width, height } = this.scene.scale;
    const margin = 40;

    // Floor
    this.graphics.fillStyle(floorColor, 1);
    this.graphics.fillRect(margin, margin, width - margin * 2, height - margin * 2);

    // Walls
    this.graphics.fillStyle(wallColor, 1);
    this.graphics.fillRect(margin, margin, width - margin * 2, 8); // top
    this.graphics.fillRect(margin, height - margin - 8, width - margin * 2, 8); // bottom
    this.graphics.fillRect(margin, margin, 8, height - margin * 2); // left
    this.graphics.fillRect(width - margin - 8, margin, 8, height - margin * 2); // right
  }

  protected addExitButton(): void {
    const exitBtn = this.scene.add.text(60, 15, '<- Sortir', {
      fontSize: '16px',
      fontFamily: 'monospace',
      color: '#ffffff',
      backgroundColor: '#333333',
      padding: { x: 10, y: 6 },
    }).setOrigin(0, 0.5).setInteractive({ useHandCursor: true }).setDepth(100);

    exitBtn.on('pointerdown', () => this.exitCallback?.());
    exitBtn.on('pointerover', () => exitBtn.setColor('#ff6b35'));
    exitBtn.on('pointerout', () => exitBtn.setColor('#ffffff'));

    this.elements.push(exitBtn);
  }

  protected addTitle(text: string): void {
    const { width } = this.scene.scale;
    const title = this.scene.add.text(width / 2, 55, text, {
      fontSize: '22px',
      fontFamily: 'monospace',
      color: '#ffffff',
    }).setOrigin(0.5).setDepth(100);
    this.elements.push(title);
  }

  protected addLabel(x: number, y: number, text: string, color = '#aaaaaa', size = '12px'): Phaser.GameObjects.Text {
    const label = this.scene.add.text(x, y, text, {
      fontSize: size,
      fontFamily: 'monospace',
      color,
    }).setDepth(100);
    this.elements.push(label);
    return label;
  }

  protected addButton(
    x: number, y: number, text: string, color: string, onClick: () => void,
  ): Phaser.GameObjects.Text {
    const btn = this.scene.add.text(x, y, text, {
      fontSize: '14px',
      fontFamily: 'monospace',
      color,
      backgroundColor: '#222222',
      padding: { x: 10, y: 6 },
    }).setOrigin(0.5).setInteractive({ useHandCursor: true }).setDepth(100);

    btn.on('pointerdown', onClick);
    btn.on('pointerover', () => btn.setAlpha(0.8));
    btn.on('pointerout', () => btn.setAlpha(1));

    this.elements.push(btn);
    return btn;
  }

  protected drawProgressBar(
    x: number, y: number, width: number, height: number,
    progress: number, bgColor: number, fillColor: number,
  ): void {
    this.graphics.fillStyle(bgColor, 1);
    this.graphics.fillRect(x, y, width, height);
    this.graphics.fillStyle(fillColor, 1);
    this.graphics.fillRect(x, y, width * Math.min(1, progress), height);
  }

  destroy(): void {
    this.graphics.destroy();
    for (const el of this.elements) {
      el.destroy();
    }
    this.elements = [];
  }
}
